import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "../i18n/useTranslation";
import PhoneInput from "./PhoneInput";

export default function Sidebar({
  nodeText,
  setNodeText,
  nodePrompt,
  setNodePrompt,
  nodePromptInitialMessage,
  setNodePromptInitialMessage,
  nodePromptSystemInstruction,
  setNodePromptSystemInstruction,
  nodeTool,
  setNodeTool,
  nodeToolDescription,
  setNodeToolDescription,
  nodeIntent,
  setNodeIntent,
  nodeIntentDescription,
  setNodeIntentDescription,
  nodeIntentSpeak,
  setNodeIntentSpeak,
  nodeIntentForwardPhone,
  setNodeIntentForwardPhone,
  selectedNode,
  setSelectedElements,
  edges,
  nodes,
  nodeAgentName,
  setNodeAgentName,
  nodeMuteUser,
  setNodeMuteUser,
}) {
  const t = useTranslation();
  const [hasConnectedPrompt, setHasConnectedPrompt] = useState(false);
  const prevConnectionState = useRef(false);
  
  // Add a render counter
  const renderCount = useRef(0);
  useEffect(() => {
    renderCount.current += 1;
  });

  // Check if the selected intent node has a connected prompt node
  useEffect(() => {
    // Only proceed if we have a selected intent node
    if (!selectedNode || selectedNode.type !== "intentnode") {
      setHasConnectedPrompt(false);
      return;
    }

    // Make sure both edges and nodes arrays exist
    if (!edges || !nodes) {
      setHasConnectedPrompt(false);
      return;
    }
    
    // Check if the intent node has a connection to a prompt node
    const hasPromptConnection = edges.some(edge => 
      edge.source === selectedNode.id && 
      (edge.target.startsWith('prompt_') || 
       nodes.some(n => n.id === edge.target && n.type === 'promptnode'))
    );
    
    // Update the connection state
    setHasConnectedPrompt(hasPromptConnection);
    
    // If the connection state changed, update the node data
    if (prevConnectionState.current !== hasPromptConnection) {
      prevConnectionState.current = hasPromptConnection;
      
      // Update the node data to reflect the connected state
      setSelectedElements((prevElements) => {
        const updatedElements = [...prevElements];
        if (updatedElements.length > 0 && updatedElements[0].id === selectedNode.id) {
          updatedElements[0] = { 
            ...updatedElements[0], 
            data: { 
              ...updatedElements[0].data,
              hasConnectedPrompt: hasPromptConnection 
            } 
          };
        }
        return updatedElements;
      });
    }
  }, [selectedNode, edges, nodes, setSelectedElements]);

  const handleInputChange = (event) => {
    setNodeText(event.target.value);
  };

  const handlePromptChange = (event) => {
    setNodePrompt(event.target.value);
    // store prompt in the selected node
    setSelectedElements((prevElements) => {
      const updatedElements = [...prevElements];
      updatedElements[selectedNode.id] = { ...selectedNode, prompt: event.target.value };
      return updatedElements;
    });
  };

  const handlePromptInitialMessageChange = (event) => {
    setNodePromptInitialMessage(event.target.value);
    setSelectedElements((prevElements) => {
      const updatedElements = [...prevElements];
      updatedElements[selectedNode.id] = { ...selectedNode, promptInitialMessage: event.target.value };
      return updatedElements;
    });
  };

  const handlePromptSystemInstructionChange = (event) => {
    setNodePromptSystemInstruction(event.target.value);
    setSelectedElements((prevElements) => {
      const updatedElements = [...prevElements];
      updatedElements[selectedNode.id] = { ...selectedNode, data: { ...selectedNode.data, promptSystemInstruction: event.target.value } };
      return updatedElements;
    });
  };


  const handleIntentChange = (event) => {
    setNodeIntent(event.target.value)
  };

  const handleIntentDescriptionChange = (event) => {
    setNodeIntentDescription(event.target.value);
    // save intent description in the selected node
    setSelectedElements((prevElements) => {
      const updatedElements = [...prevElements];
      updatedElements[selectedNode.id] = {
        ...selectedNode,
        data: { ...selectedNode.data, intentDescription: event.target.value }
      };
      return updatedElements;
    });
  };

  const handleIntentSpeakChange = (event) => {
    setNodeIntentSpeak(event.target.value);
    setSelectedElements((prevElements) => {
      const updatedElements = [...prevElements];
      updatedElements[selectedNode.id] = { ...selectedNode, data: { ...selectedNode.data, intentSpeak: event.target.value } };
      return updatedElements;
    });
  };

  const handleAgentNameChange = (event) => {
    setNodeAgentName(event.target.value);
  };

  const handleMuteChange = (event) => {
    setNodeMuteUser(event.target.checked);
  };

  const [activeTab, setActiveTab] = useState("details");
  const [activeTabIntent, setActiveTabIntent] = useState("intent");

  return (
    <aside className={`
      border-r-2 border-gray-200 
      p-4 text-sm bg-gray-100 text-gray-700
      fixed right-0 top-0 h-screen
      transform transition-transform duration-300 ease-in-out
      ${selectedNode ? 'translate-x-0 w-1/3' : 'translate-x-full w-0'}
    `}>
      {selectedNode ? (
        <div>
          <div className="mb-2 text-blue-600">{t('editNode')}</div>
          <hr></hr>
          <br></br>
          {selectedNode.type === "promptnode" ? (
            <div>
              <div className="flex mb-4">
                <button
                  className={`px-4 py-2 ${activeTab === "details" ? "bg-gray-300 rounded-lg mr-2 border border-blue-600" : "bg-gray-200 rounded-lg mr-2"}`}
                  onClick={() => setActiveTab("details")}
                >
                  {t('promptNode.title')}
                </button>
                <button
                  className={`px-4 py-2 ${activeTab === "initialMessage" ? "bg-gray-300 rounded-lg mr-2 border border-blue-600" : "bg-gray-200 rounded-lg mr-2"}`}
                  onClick={() => setActiveTab("initialMessage")}
                >
                  {t('settings')}
                </button>
              </div>

              {activeTab === "details" && (
                <div>
                  <label className="block mb-2 text-sm text-gray-900 font-light pb-2">{t('name')}:</label>
                  <input
                    type="text"
                    className="block w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-gray-500"
                    value={nodeText}
                    onChange={handleInputChange}
                  />
                  <br></br>
                  <label className="block mb-2 text-sm font-light text-gray-900 pb-2">{t('promptNode.content')}:</label>
                  <textarea
                    className="block w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-gray-500"
                    value={nodePrompt}
                    onChange={handlePromptChange}
                    rows={20}
                  />
                </div>
              )}

              {activeTab === "initialMessage" && (
                <div>
                  <label className="block mb-2 text-sm font-light text-gray-900 pb-2">{t('promptNode.initialMessage')}:</label>
                  <input
                    type="text"
                    className="block w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-gray-500"
                    value={nodePromptInitialMessage || ""}
                    onChange={handlePromptInitialMessageChange}
                  />
                  <br></br>
                  <label className="block mb-2 text-sm font-light text-gray-900 pb-2">{t('promptNode.systemInstruction')}</label>
                  <div className="bg-yellow-50 p-3 mb-4 rounded border border-yellow-200">
                    <div className="text-xs text-yellow-800">
                      <span className="font-medium">{t('notice')}:</span>
                      <p className="mt-1">
                        {t('promptNode.systemInstructionNotice')}
                      </p>
                    </div>
                  </div>
                  <textarea
                    className="block w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-gray-500 placeholder-gray-300"
                    value={nodePromptSystemInstruction || ""}
                    onChange={handlePromptSystemInstructionChange}
                    placeholder={t('promptNode.systemInstructionPlaceholder')}
                    rows={11}
                  />
                  <br></br>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="promptMute"
                      className="mr-2"
                      checked={nodeMuteUser || false}
                      onChange={handleMuteChange}
                    />
                    <label htmlFor="promptMute" className="text-sm text-gray-900">
                      {t('muteUser')}
                    </label>
                  </div>
                </div>
              )}
            </div>
          ) : selectedNode.type === "numbernode" ? (
            <div>
              <label className="block mb-2 text-sm font-light text-gray-900 pb-2">{t('voiceAgentKey')}:</label>
              <input
                type="text"
                className="block w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-gray-500"
                value={nodeAgentName || ""}
                onChange={handleAgentNameChange}
                placeholder={t('defaultAgentName')}
              />
            </div>
          ) : selectedNode.type === "toolnode" ? (
            <div>
              <div className="bg-gray-50 p-3 mb-4 rounded border border-gray-200">
                <div className="text-xs text-gray-600">
                  <span className="font-medium">{t('notice')}:</span>
                  <p className="mt-1">
                    {t('toolNode.readOnlyNotice')}
                  </p>
                </div>
              </div>
              <label className="block mb-2 text-sm font-medium text-gray-900">{t('toolNode.title')}:</label>
              <input
                type="text"
                className="block w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg bg-gray-200 cursor-not-allowed"
                value={nodeTool || ""}
                readOnly
                disabled
              />
              <br></br>
              <label className="block mb-2 text-sm font-light text-gray-900 pb-2">{t('toolNode.description')}:</label>
              <textarea
                className="block w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg bg-gray-200 cursor-not-allowed"
                value={nodeToolDescription || ""}
                readOnly
                disabled
                rows={20}
              />
            </div>
          ) : selectedNode.type === "intentnode" ? (
            <div>
              <div className="flex mb-4">
                <button
                  className={`px-4 py-2 ${activeTabIntent === "intent" ? "bg-gray-300 rounded-lg mr-2 border border-blue-600" : "bg-gray-200 rounded-lg mr-2"}`}
                  onClick={() => setActiveTabIntent("intent")}
                >
                  {t('intentNode.title')}
                </button>
                <button
                  className={`px-4 py-2 ${activeTabIntent === "intentSettings" ? "bg-gray-300 rounded-lg mr-2 border border-blue-600" : "bg-gray-200 rounded-lg mr-2"}`}
                  onClick={() => setActiveTabIntent("intentSettings")}
                >
                  {t('settings')}
                </button>
              </div>

              {activeTabIntent === "intent" && (
                <div>
                  <label className="block mb-2 text-sm text-gray-900 pb-2">{t('intentNode.title')}:</label>
                  <input
                    type="text"
                    className={`w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-gray-500`}
                    value={nodeIntent || ""}
                    onChange={handleIntentChange}
                  />
                  <br></br><br></br>
                  <label className="block mb-2 text-sm text-gray-900 pb-2">{t('intentNode.content')}:</label>
                  <textarea
                    className="block w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-gray-500"
                    value={nodeIntentDescription || ""}
                    onChange={handleIntentDescriptionChange}
                    rows={10}
                  />
                </div>
              )}

              {activeTabIntent === "intentSettings" && (
                <div>
                  <div className="bg-blue-50 p-3 mb-4 rounded border border-blue-200">
                    <div className="text-xs text-blue-800">
                      <span className="font-medium">{t('intentNode.connectionInstructions')}</span>
                      <p className="mt-1">
                        {t('intentNode.dragPromptExplanation')}
                      </p>
                    </div>
                  </div>
                  
                  {hasConnectedPrompt && (
                    <div className="bg-yellow-50 p-3 mb-4 rounded border border-yellow-200">
                      <div className="text-xs text-yellow-800">
                        <span className="font-medium">{t('notice')}</span>
                        <p className="mt-1">
                          {t('intentNode.promptConnectedNotice')}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <label className="block mb-2 text-sm text-gray-900 pb-2">{t('intentNode.speak')}</label>
                  <input
                    type="text"
                    className={`block w-full pt-2 px-3 pb-3 text-gray-700 border border-gray-300 rounded-lg ${hasConnectedPrompt ? 'bg-gray-200 cursor-not-allowed' : 'bg-white focus:outline-none focus:border-gray-500'}`}
                    value={nodeIntentSpeak || ""}
                    onChange={handleIntentSpeakChange}
                    disabled={hasConnectedPrompt}
                    required={!hasConnectedPrompt}
                  />
                  <br></br>
                  <PhoneInput
                    label={t('intentNode.forwardPhone')}
                    value={nodeIntentForwardPhone || ""}
                    onChange={setNodeIntentForwardPhone}
                    placeholder={t('phoneNumberPlaceholder')}
                    disabled={hasConnectedPrompt}
                    showLabel={true}
                  />
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}
